import { z } from 'zod';

import { fitsQuotationDecimal } from '#/modules/crmMgmt/quotation/helper/quotationLines';

export const QUOTATION_FIELD_LABELS = {
  customerId: '客戶',
  contactName: '聯絡人',
  contactPhone: '聯絡電話',
  customerAddress: '客戶地址',
  paymentTermId: '付款條件',
  roundingMode: '總額取整',
  remark: '內部備註',
  lines: '報價項目',
};

const LINE_FIELD_LABELS = {
  itemName: '品名',
  qty: '數量',
  unitPrice: '單價',
};

export function resolveQuotationFieldLabel(path) {
  const parts = String(path).split('.');
  if (parts[0] === 'lines' && parts[2] === 'subLines' && parts.length >= 5) {
    return `第 ${Number(parts[1]) + 1} 項子項目 ${Number(parts[3]) + 1} ${LINE_FIELD_LABELS[parts[4]] ?? parts[4]}`;
  }
  if (parts[0] === 'lines' && parts.length >= 3) {
    return `第 ${Number(parts[1]) + 1} 項 ${LINE_FIELD_LABELS[parts[2]] ?? parts[2]}`;
  }
  return QUOTATION_FIELD_LABELS[parts[0]] ?? path;
}

const decimal = z.union([z.string(), z.number(), z.null()]).optional();

function checkQty(value, ctx, path) {
  if (value === '' || value == null) {
    return;
  }
  const qty = Number(value);
  if (Number.isNaN(qty) || qty <= 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: '數量須大於 0', path });
  } else if (!fitsQuotationDecimal(value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: '數量最多四位小數、16 位整數', path });
  }
}

const subLineSchema = z.object({
  key: z.string(),
  subLineId: z.string().nullable().optional(),
  productNo: z.string().optional().nullable(),
  itemName: z.string().trim().min(1, '子項目品名必填'),
  qty: decimal,
  unitName: z.string().optional().nullable(),
  customerRemark: z.string().optional().nullable(),
  internalRemark: z.string().optional().nullable(),
}).superRefine((subLine, ctx) => {
  checkQty(subLine.qty, ctx, ['qty']);
});

/** 主項目草稿可不完整（不計價）；有值才驗數量 > 0、單價 ≥ 0。 */
const lineSchema = z.object({
  key: z.string(),
  lineId: z.string().nullable().optional(),
  productNo: z.string().optional().nullable(),
  itemName: z.string().optional().nullable(),
  qty: decimal,
  unitName: z.string().optional().nullable(),
  unitPrice: decimal,
  customerRemark: z.string().optional().nullable(),
  internalRemark: z.string().optional().nullable(),
  subLines: z.array(subLineSchema),
}).superRefine((line, ctx) => {
  checkQty(line.qty, ctx, ['qty']);
  if (line.unitPrice !== '' && line.unitPrice != null) {
    const price = Number(line.unitPrice);
    if (Number.isNaN(price) || price < 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '單價不可為負', path: ['unitPrice'] });
    } else if (!fitsQuotationDecimal(line.unitPrice)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '單價最多四位小數、16 位整數', path: ['unitPrice'] });
    }
  }
});

export const quotationFormSchema = z.object({
  customerId: z.string().min(1, '請選擇客戶'),
  contactName: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
  customerAddress: z.string().optional().nullable(),
  paymentTermId: z.string().optional().nullable(),
  roundingMode: z.string().min(1, '請選擇總額取整方式'),
  remark: z.string().optional().nullable(),
  lines: z.array(lineSchema),
});
